import type { PricesSlice } from '@hooks/states/pricesSlice';
import { createPriceDataSlice } from '@hooks/states/pricesSlice';
import type { ProductPageProps } from '@product/ProductPage/ProductPage';
import React from 'react';
import type { StoreApi } from 'zustand';
import create from 'zustand';
import createContext from 'zustand/context';
import type { ProductConfigSlice } from './productConfigSlice';
import { createProductConfigSlice } from './productConfigSlice';
import type { ProductDataSlice, ProductDataSliceData } from './productDataSlice';
import { createProductDataSlice } from './productDataSlice';
import type { VariantSelectionSlice } from './variantSelectionSlice';
import { createVariantSelectionSlice } from './variantSelectionSlice';
import type { OverstockSaleSlice } from './overstockSaleSlice';
import { createOverstockSaleSlice } from './overstockSaleSlice';

export type ProductPageStore = ProductDataSlice &
  VariantSelectionSlice &
  ProductConfigSlice &
  PricesSlice &
  OverstockSaleSlice;

/*
 * As we use NEXTJS and static generation with data (Basic Features: Pages | Next.js),
 * we can’t initiate Zustand Store globally, because the initial data for the store
 * initialization comes directly to the component as props. Thus, for our application architecture,
 * the only way to have a Zustand store hook available along the components is to use Zustand Context.
 *
 * Note: Zustand `createContext` method is deprecated and will be removed in version 5.
 */
export const createStore = (initialData: ProductPageProps) =>
  create<ProductPageStore>((set, get) => ({
    ...createProductDataSlice(set, get, initialData),
    ...createVariantSelectionSlice(set, get, initialData),
    ...createProductConfigSlice(set, initialData),
    ...createPriceDataSlice(set, get, initialData),
    ...createOverstockSaleSlice(set),
  }));

export const { Provider, useStore } = createContext<StoreApi<ProductPageStore>>();

export const useProductPageStore = useStore;

type ProductPageStoreProviderProps = {
  initialData: ProductPageProps & Partial<ProductDataSliceData>;
  children?: React.ReactNode;
};

export const ProductPageStoreProvider = ({ children, initialData }: ProductPageStoreProviderProps) => (
  <Provider createStore={() => createStore(initialData)}>{children}</Provider>
);
